import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: DieselTaxState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<DieselTaxState, RootState> = {
  all(state: DieselTaxState): DieselTax[] {
    return state.all;
  },
  viewing(state: DieselTaxState): DieselTax | undefined {
    return state.viewing;
  },
};

const mutations: MutationTree<DieselTaxState> = {
  RESET(state: DieselTaxState) {
    state.all = [];
  },
  ADD_MODEL(state: DieselTaxState, payload: DieselTax) {
    state.all.push(payload);
  },
  ADD_MODELS(state: DieselTaxState, payload: DieselTax[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: DieselTaxState, payload: DieselTax[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: DieselTaxState, payload: DieselTax) {
    const index = state.all.findIndex((model: DieselTax) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: DieselTaxState, payload: number) {
    state.all = state.all.filter((model: DieselTax) => model.id !== payload);
  },
  SET_VIEWING(state: DieselTaxState, payload: DieselTax) {
    state.viewing = payload;
  },
};

const resource: string = 'diesel-taxes';

const actions: ActionTree<DieselTaxState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: DieselTaxCreateData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index({ commit, rootState }, { query = { s: { created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<DieselTax>; workspaceId: number }) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax[]; total: number }) => {
        if (query.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }
        return Promise.resolve(response);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: DieselTaxUpdateData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  patch({ commit, rootState }, { id, data, workspaceId }: { id: number; data: Partial<DieselTaxUpdateData>; workspaceId: number }) {
    return rootState.api
      .patch(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: DieselTax }) => {
        commit('REMOVE_MODEL', id);

        return Promise.resolve(id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  reset({ commit, rootState }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
